import { Trans, useTranslation } from 'react-i18next';
import { match, Pattern } from 'ts-pattern';

import type { SubmittedInvoice } from '~/api/invoices/types';
import type { IconName } from '~/components';

import { ExternalLink, StatusCard, Text } from '~/components';

type Props = {
  invoice: SubmittedInvoice;
};

export const InvoiceStatusCard = ({ invoice }: Props) => {
  const { t } = useTranslation(['common', 'invoices']);

  const icon = match(invoice)
    .returnType<IconName>()
    .with({ status: 'InReview' }, () => 'CheckProgress')
    .with({ status: 'LockedByUser' }, () => 'Edit')
    .with({ status: 'Expired', hasPaymentFollowUp: true }, () => 'Gavel')
    .with({ status: Pattern.union('Approved', 'Expired'), coPartiallyPaid: true }, () => 'Payments')
    .with({ status: 'Approved', coTransportType: 'Peppol' }, () => 'Peppol')
    .with({ status: 'Approved' }, () => 'Send')
    .with({ status: 'Expired' }, () => 'HourglassBottom')
    .with({ status: 'CoPaid' }, () => 'Payments')
    .with({ status: 'FcPaid' }, () => 'Payments')
    .with({ status: 'Credited' }, () => 'CreditNote')
    .exhaustive();

  const title = match(invoice)
    .with({ status: 'Approved', coTransportType: 'Peppol' }, () => t('invoices:details.statusInfo.title.sentWithPeppol'))
    .with({ status: 'Expired', hasPaymentFollowUp: true }, () => t('invoices:details.statusInfo.title.paymentFollowUp'))
    .with({ status: 'InReview' }, () => t('invoices:details.statusInfo.title.inReview'))
    .with({ status: Pattern.union('Approved', 'Expired'), coPartiallyPaid: true }, () =>
      t('invoices:overview.columns.substatus.CoPartiallyPaid'),
    )
    .otherwise(() => t(`invoices:overview.columns.status.options.${invoice.status}`));

  return (
    <StatusCard icon={icon} title={title}>
      <Text>
        <Trans
          components={{ a: <ExternalLink />, br: <br /> }}
          i18nKey={match(invoice)
            .with({ status: 'InReview' }, () => 'invoices:details.statusInfo.message.InReview.default' as const)
            .with({ status: 'LockedByUser' }, () => 'invoices:details.statusInfo.message.LockedByUser.default' as const)
            .with(
              { status: 'Approved', coPartiallyPaid: true },
              () => 'invoices:details.statusInfo.message.Expired.coPartiallyPaid' as const,
            )
            .with({ status: 'Approved' }, () => 'invoices:details.statusInfo.message.Approved.default' as const)
            .with({ status: 'Expired', hasPaymentFollowUp: true }, () => 'invoices:details.statusInfo.message.Expired.followingUp' as const)
            .with(
              { status: 'Expired', coPartiallyPaid: true },
              () => 'invoices:details.statusInfo.message.Expired.coPartiallyPaid' as const,
            )
            .with({ status: 'Expired' }, () => 'invoices:details.statusInfo.message.Expired.default' as const)
            .with({ status: 'CoPaid' }, () => 'invoices:details.statusInfo.message.CoPaid.default' as const)
            .with({ status: 'FcPaid' }, () => 'invoices:details.statusInfo.message.FcPaid.default' as const)
            .with({ status: 'Credited' }, () => 'invoices:details.statusInfo.message.Credited.default' as const)
            .exhaustive()}
          values={match(invoice)
            .with({ status: 'Credited' }, ({ creditNoteDetails }) => ({
              creditNoteNumber: creditNoteDetails?.fcEntryNumber ?? '',
              creditNoteReason: creditNoteDetails?.reason ?? '',
            }))
            .otherwise(() => undefined)}
        />
      </Text>
    </StatusCard>
  );
};
